/** Regional locator map with real country outlines — self-hosted, zero CDN.
 *
 * Outlines come from world-atlas countries-110m, decoded at build time into
 * apps/web/src/assets/world-outlines.json (see git history / ops notes) and
 * bundled. Rendered with a local equirectangular projection centred on the
 * pin, framed wide enough that surrounding countries are distinguishable. */
import world from "../assets/world-outlines.json";

interface LocationMapProps {
  lat: number | null;
  lon: number | null;
  city: string;
  cc: string;
}

interface WorldData {
  f: { i: string; p: number[][] }[];
}

const W = 800;
const H = 210;
/** Vertical span of the frame in degrees latitude. Width follows from the
 *  box aspect and the cos(lat) correction — ~50–70° of longitude in Europe,
 *  enough to show a country's neighbours on both sides. */
const LAT_SPAN = 14;

export function LocationMap({ lat, lon, city, cc }: LocationMapProps) {
  const hasPin = lat != null && lon != null;
  const lat0 = hasPin ? (lat as number) : 30;
  const lon0 = hasPin ? (lon as number) : 10;

  // px per degree; x compressed by cos(lat0) so shapes keep local aspect.
  const s = H / LAT_SPAN;
  const kx = s * Math.max(0.35, Math.cos((lat0 * Math.PI) / 180));
  const px = (lonD: number) => W / 2 + (lonD - lon0) * kx;
  const py = (latD: number) => H / 2 + (lat0 - latD) * s;

  // Cull countries entirely outside the frame (with margin), then build paths.
  const lonHalf = W / 2 / kx + 4;
  const latHalf = LAT_SPAN / 2 + 4;
  const paths: string[] = [];
  for (const f of (world as WorldData).f) {
    for (const poly of f.p) {
      let inFrame = false;
      for (let i = 0; i < poly.length; i += 2) {
        const dLon = Math.abs((poly[i]! - lon0 + 540) % 360 - 180);
        if (dLon <= lonHalf && Math.abs(poly[i + 1]! - lat0) <= latHalf) {
          inFrame = true;
          break;
        }
      }
      if (!inFrame) continue;
      // Split the outline where it wraps the antimeridian (e.g. Russia, Fiji) —
      // otherwise the wrap draws a streak clear across the frame.
      let d = "";
      let prevLon: number | null = null;
      for (let i = 0; i < poly.length; i += 2) {
        const lonD = poly[i]!;
        const wrap = prevLon !== null && Math.abs(lonD - prevLon) > 180;
        d += `${i === 0 || wrap ? "M" : "L"}${px(lonD).toFixed(1)},${py(poly[i + 1]!).toFixed(1)}`;
        prevLon = lonD;
      }
      paths.push(d);
    }
  }

  const cx = hasPin ? px(lon as number) : null;
  const cy = hasPin ? py(lat as number) : null;
  const label = city ? `${city}${cc ? `, ${cc}` : ""}` : "";

  return (
    <div style={{ marginTop: 4, border: "1px solid #2A2D29", borderRadius: 16, overflow: "hidden", background: "#0A0B0A" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: 210, display: "block" }}
        role="img"
        aria-label={label ? `Approximate location: ${label}` : "Location map"}
      >
        <rect x={0} y={0} width={W} height={H} fill="#0A0B0A" />
        {paths.map((d, i) => (
          <path key={i} d={d} fill="#141614" stroke="rgba(255,255,255,0.55)" strokeWidth={0.6} strokeLinejoin="round" />
        ))}
        {cx != null && cy != null && (
          <g>
            <circle cx={cx} cy={cy} r={16} fill="#DCF300" opacity={0.16} />
            <circle cx={cx} cy={cy} r={9} fill="none" stroke="#DCF300" strokeWidth={1.5} />
            <circle cx={cx} cy={cy} r={4} fill="#DCF300" />
            {label && (
              <text
                x={cx + (cx > W - 130 ? -12 : 12)}
                y={cy + 4}
                textAnchor={cx > W - 130 ? "end" : "start"}
                fill="#DEDFDF"
                style={{ font: "12px 'Space Mono', monospace" }}
              >
                {label}
              </text>
            )}
          </g>
        )}
      </svg>
    </div>
  );
}
